// The AI Metrickle runs at its own expense: which model does each job, which models it may switch
// to, and why. The one source for the API's calls (apps/api/src/lib/ai-models.ts), AI-credit
// pricing (apps/api/src/lib/ai-credits.ts), the growth console's AI models page and the public
// /trust/ai page.
//
// To change a job's model for good: add a decision to its `decisions` (newest last), with the
// date and a reason in plain words, since it's published. A platform admin can also switch a job
// to another of its `models` from the growth console without a deploy (an outage, a quick trial);
// that switch and its reason are published on /trust/ai too, until it's reset.

export const AI_MODEL_IDS = ["claude-opus-5-5", "claude-sonnet-5-5", "claude-haiku-5-5", "claude-haiku-4-5-20251001"] as const;
export type AiModelId = (typeof AI_MODEL_IDS)[number];

export interface AiModel {
  name: string;
  provider: "Anthropic";
  /** US dollars per million tokens, Claude API list prices. */
  price: { input: number; output: number };
  /** Takes `output_config.effort`. */
  effort: boolean;
  /** Takes server-side refusal fallbacks (`fallbacks: "default"`). */
  fallbacks: boolean;
  /** Reads images. */
  vision: boolean;
}

export const AI_MODELS: Record<AiModelId, AiModel> = {
  "claude-opus-5-5": { name: "Claude Opus 5.5", provider: "Anthropic", price: { input: 4, output: 20 }, effort: true, fallbacks: true, vision: true },
  "claude-sonnet-5-5": { name: "Claude Sonnet 5.5", provider: "Anthropic", price: { input: 2, output: 10 }, effort: true, fallbacks: true, vision: true },
  // Up to 100K input tokens; longer prompts cost more, and no job here sends one that long.
  "claude-haiku-5-5": { name: "Claude Haiku 5.5", provider: "Anthropic", price: { input: 0.1, output: 0.5 }, effort: true, fallbacks: false, vision: true },
  "claude-haiku-4-5-20251001": { name: "Claude Haiku 4.5", provider: "Anthropic", price: { input: 1, output: 5 }, effort: false, fallbacks: false, vision: true },
};

export const isAiModelId = (v: unknown): v is AiModelId => (AI_MODEL_IDS as readonly unknown[]).includes(v);

export const AI_JOB_IDS = ["release_cause", "ticket_sort", "outreach_draft", "social_draft"] as const;
export type AiJobId = (typeof AI_JOB_IDS)[number];

export interface AiDecision {
  /** YYYY-MM-DD. */
  date: string;
  model: AiModelId;
  /** Why, in plain words. Published on /trust/ai. */
  why: string;
}

export interface AiJob {
  name: string;
  /** "customer": runs on a customer's data and counts against their AI credits. "internal": Metrickle's own team tools. */
  audience: "customer" | "internal";
  /** What the job does, in a sentence. */
  does: string;
  /** How hard the model thinks, where the model takes it. */
  effort: "low" | "medium" | "high";
  /** Needs a model that reads images. */
  vision?: boolean;
  /**
   * Keep prompts and answers in Cloudflare AI Gateway's logs. Off for any job that sends customer
   * or personal data: those log only model, tokens, cost and timing.
   */
  logPrompts: boolean;
  /** Models a platform admin may switch to. The latest decision's model is always one of them. */
  models: readonly AiModelId[];
  /** Newest last. The last one is the job's default model. */
  decisions: readonly AiDecision[];
}

export const AI_JOBS: Record<AiJobId, AiJob> = {
  release_cause: {
    name: "Explaining a release's drop",
    audience: "customer",
    does: "Reads the few changed files most likely behind a drop and names the likely cause in plain words, quoting a changed line, with the smallest fix.",
    effort: "medium",
    logPrompts: false,
    models: ["claude-opus-5-5", "claude-sonnet-5-5"],
    decisions: [
      {
        date: "2026-10-07",
        model: "claude-opus-5-5",
        why: "Reading a diff and tying it to a drop for one group of people is the hardest reasoning we ask for, and it runs rarely, so we use the strongest model. Every answer must quote a changed line, which Metrickle checks before showing it.",
      },
    ],
  },
  ticket_sort: {
    name: "Sorting support tickets",
    audience: "customer",
    does: "Picks one of five feedback categories for a support ticket that no tag rule matched, from its masked subject and the start of its first message.",
    effort: "low",
    logPrompts: false,
    models: ["claude-haiku-4-5-20251001", "claude-haiku-5-5", "claude-sonnet-5-5"],
    decisions: [
      {
        date: "2026-10-08",
        model: "claude-haiku-4-5-20251001",
        why: "A five-way choice on a short ticket doesn't need a large model. Haiku does it quickly at a fraction of the cost, so sorting a ticket costs one AI credit.",
      },
    ],
  },
  outreach_draft: {
    name: "Drafting Metrickle's emails to its customers",
    audience: "internal",
    does: "Drafts an email from Metrickle's team to a Metrickle customer, from account-level information. A person reviews every draft before it's sent.",
    effort: "low",
    logPrompts: false,
    models: ["claude-opus-5-5", "claude-sonnet-5-5"],
    decisions: [{ date: "2026-10-01", model: "claude-opus-5-5", why: "Few drafts, each read by a person, so we use the model that writes best." }],
  },
  social_draft: {
    name: "Drafting Metrickle's social posts",
    audience: "internal",
    does: "Drafts X and LinkedIn posts about a Metrickle product update, with alt text for its image. A person reviews every draft before it's posted.",
    effort: "medium",
    vision: true,
    logPrompts: true,
    models: ["claude-opus-5-5", "claude-sonnet-5-5"],
    decisions: [{ date: "2026-10-07", model: "claude-opus-5-5", why: "Few drafts, each read by a person, and it has to describe an image well for alt text, so we use the strongest model." }],
  },
};

export const isAiJobId = (v: unknown): v is AiJobId => (AI_JOB_IDS as readonly unknown[]).includes(v);

/** The job's model unless a platform admin has switched it. */
export const defaultModel = (job: AiJobId): AiModelId => AI_JOBS[job].decisions.at(-1)!.model;

/** A live switch made from the growth console (apps/api/src/lib/ai-models.ts), as the public endpoint returns it. */
export interface AiModelSwitch {
  model: AiModelId;
  reason: string;
  /** Unix ms. */
  since: number;
}

/** GET /api/ai-models: each job's model now, and the switch behind it if it isn't the default. */
export interface AiModelsLive {
  jobs: Record<AiJobId, { model: AiModelId; switched: AiModelSwitch | null }>;
}
